import Cookie from 'js-cookie';

import { injectable } from '@core/di/di-utils';

import {
  IStorageService,
  StorageType,
  StorageKey,
  StorageSetOptions,
} from '@shared/types/services/storage';

type Methods = {
  get: (key: StorageKey) => any | undefined;
  set: (key: StorageKey, value: any, options?: StorageSetOptions) => void;
  remove: (key: StorageKey) => void;
};

@injectable()
export class StorageService implements IStorageService {
  private memoryStorage: Record<string, any> = {};

  private get methods(): { [type in StorageType]: Methods } {
    return {
      memoryStorage: {
        get: (key: StorageKey) => this.memoryStorage[key],
        set: (key: StorageKey, value: any) => {
          this.memoryStorage[key] = value;
        },
        remove: (key: StorageKey) => {
          delete this.memoryStorage[key];
        },
      },
      localStorage: {
        get: this.getLocalStorageValue,
        set: this.setLocalStorageValue,
        remove: window.localStorage.removeItem.bind(window.localStorage),
      },
      sessionStorage: {
        get: this.getSessionStorageValue,
        set: this.setSessionStorageValue,
        remove: window.sessionStorage.removeItem.bind(window.sessionStorage),
      },
    };
  }

  get: IStorageService['get'] = (type, key) => {
    const value =
      this.methods[type].get(this.getStorageKey(key)) ||
      this.methods.memoryStorage.get(this.getStorageKey(key));

    try {
      return value ? JSON.parse(value) : value;
    } catch {
      return value;
    }
  };

  set: IStorageService['set'] = (type, key, value, options) => {
    const storageKey = this.getStorageKey(key);

    this.methods[type].set(storageKey, JSON.stringify(value), {
      options,
    });
    const storedValue = this.methods[type].get(storageKey);

    if (!storedValue) {
      this.methods.memoryStorage.set(storageKey, JSON.stringify(value));
    }
  };

  remove: IStorageService['remove'] = (type, key) => {
    this.methods[type].remove(this.getStorageKey(key));
  };

  private getStorageKey(key: StorageKey) {
    return `${window.location.origin}_${key}`;
  }

  private setLocalStorageValue<T>(key: StorageKey, value: T): void {
    const stringValue = typeof value === 'string' ? value : JSON.stringify(value);
    localStorage.setItem(key, stringValue);
  }

  private getLocalStorageValue<T>(key: StorageKey): T | undefined {
    const stringValue = localStorage.getItem(key);
    if (stringValue === null) {
      return undefined;
    }
    try {
      const parsedValue = JSON.parse(stringValue);
      return parsedValue as T;
    } catch (error) {
      return stringValue as unknown as T;
    }
  }

  private setSessionStorageValue<T>(key: StorageKey, value: T): void {
    const stringValue = typeof value === 'string' ? value : JSON.stringify(value);
    sessionStorage.setItem(key, stringValue);
  }

  private getSessionStorageValue<T>(key: StorageKey): T | undefined {
    const stringValue = sessionStorage.getItem(key);
    if (stringValue === null) {
      return undefined;
    }
    try {
      const parsedValue = JSON.parse(stringValue);
      return parsedValue as T;
    } catch (error) {
      return stringValue as unknown as T;
    }
  }
}
